import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import "../../styles/MatchScreen.css";

const positions = ["GS", "GA", "WA", "C", "WD", "GD", "GK"];
const shooterPositions = ["GS", "GA"];
const positiveStats = ["intercept", "deflection", "rebound", "breakOS"];
const negativeStats = ["contact", "obstruction", "footwork", "replay", "heldBall", "lossOfPossession"];

const statActions = [
  { key: "goal", label: "Goal", section: "Shooter" },
  { key: "miss", label: "Miss", section: "Shooter" },
  { key: "intercept", label: "Intercept", section: "Positive" },
  { key: "deflection", label: "Deflection", section: "Positive" },
  { key: "rebound", label: "Rebound", section: "Positive" },
  { key: "breakOS", label: "Break/OS", section: "Positive" },
  { key: "contact", label: "Contact", section: "Negative" },
  { key: "obstruction", label: "Obstruction", section: "Negative" },
  { key: "footwork", label: "Footwork", section: "Negative" },
  { key: "replay", label: "Replay", section: "Negative" },
  { key: "heldBall", label: "Held Ball", section: "Negative" },
  { key: "lossOfPossession", label: "Loss of Possession", section: "Negative" }
];

function MatchScreen() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [club, setClub] = useState(null);
  const [team, setTeam] = useState(null);
  const [match, setMatch] = useState(null);

  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [stats, setStats] = useState({});
  const [quarter, setQuarter] = useState(1);
  const [timer, setTimer] = useState(12 * 60);
  const [timerRunning, setTimerRunning] = useState(false);
  const [events, setEvents] = useState([]);
  const [onCourt, setOnCourt] = useState([]);
  const [bench, setBench] = useState([]);
  const [showSubModal, setShowSubModal] = useState(false);
  const [subData, setSubData] = useState({});
  const [confirmingSub, setConfirmingSub] = useState(false);
  const [activePlayerCard, setActivePlayerCard] = useState(null);
  const [expandedStatsPlayer, setExpandedStatsPlayer] = useState(null);

  const navigate = useNavigate();

  // --- INITIAL DATA LOAD ---
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      setLoading(true);
      const matchRef = doc(db, "games", `demoGame_${user.uid}`);
      const teamRef = doc(db, "teams", `demoTeam_${user.uid}`);
      const clubRef = doc(db, "clubs", `demoClub_${user.uid}`);
      const matchSnap = await getDoc(matchRef);
      const teamSnap = await getDoc(teamRef);
      const clubSnap = await getDoc(clubRef);

      if (matchSnap.exists() && teamSnap.exists() && clubSnap.exists()) {
        const matchData = matchSnap.data();
        setMatch(matchData);
        setTeam(teamSnap.data());
        setClub(clubSnap.data());
        setHomeScore(matchData.homeScore || 0);
        setAwayScore(matchData.awayScore || 0);
        setStats(matchData.stats || {});
        setQuarter(matchData.quarter || 1);
        setEvents(matchData.events || []);
        setTimer((matchData.quarterLength || 12) * 60);

        // On court and bench, with minutes logic
        const now = Date.now();
        const oc = matchData.startingLineup.map((p) => ({
          ...p,
          onCourtSince: now,
          totalMinutes: matchData.stats && matchData.stats[p.playerId]?.minutes ? matchData.stats[p.playerId].minutes : 0,
        }));
        setOnCourt(oc);

        setBench((matchData.subs || []).map((p) => ({
          ...p, onCourtSince: null, totalMinutes: matchData.stats && matchData.stats[p.playerId]?.minutes ? matchData.stats[p.playerId].minutes : 0
        })));
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line
  }, [user]);

  // --- TIMER LOGIC ---
  useEffect(() => {
    let interval;
    if (timerRunning && timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    if (timer === 0 && timerRunning) {
      setTimerRunning(false);
      handleEndQuarter();
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [timerRunning, timer]);

  // --- FIRESTORE SAVE ---
  const saveMatch = async (updates) => {
    const matchRef = doc(db, "games", `demoGame_${user.uid}`);
    await updateDoc(matchRef, updates);
  };

  // --- STATS & EVENTS LOGIC ---
  const recordEvent = (action, details) => {
    const evt = { action, details, quarter, time: timer, ts: Date.now() };
    const newEvents = [...events, evt];
    setEvents(newEvents);
    saveMatch({ events: newEvents });

    setStats((prev) => {
      const key = details.playerId;
      const old = { ...(prev[key] || { goals: 0, misses: 0, minutes: prev[key]?.minutes || 0, byQuarter: {} }) };
      const q = quarter;
      old.byQuarter = old.byQuarter || {};
      old.byQuarter[q] = old.byQuarter[q] || {};
      switch (action) {
        case "goal":
          old.goals = (old.goals || 0) + 1;
          old.byQuarter[q].goals = (old.byQuarter[q].goals || 0) + 1;
          setHomeScore((n) => {
            const s = n + 1;
            saveMatch({ homeScore: s });
            return s;
          });
          break;
        case "miss":
          old.misses = (old.misses || 0) + 1;
          old.byQuarter[q].misses = (old.byQuarter[q].misses || 0) + 1;
          break;
        case "intercept":
        case "deflection":
        case "rebound":
        case "breakOS":
        case "contact":
        case "obstruction":
        case "footwork":
        case "replay":
        case "heldBall":
        case "lossOfPossession":
          old[action] = (old[action] || 0) + 1;
          old.byQuarter[q][action] = (old.byQuarter[q][action] || 0) + 1;
          break;
        case "oppositionGoal":
          setAwayScore((n) => {
            const s = n + 1;
            saveMatch({ awayScore: s });
            return s;
          });
          break;
        default: break;
      }
      if (!key) return prev;
      const nextStats = { ...prev, [key]: old };
      saveMatch({ stats: nextStats });
      return nextStats;
    });
  };

  // OPP +1 (fixed)
  const handleOppGoal = () => {
    recordEvent("oppositionGoal", {});
  };

  const handleUndo = () => {
    if (events.length === 0) return;
    const last = events[events.length - 1];
    const rest = events.slice(0, -1);

    setEvents(rest);
    saveMatch({ events: rest });

    setStats((prev) => {
      const key = last.details.playerId;
      const old = { ...(prev[key] || {}) };
      const q = last.quarter;
      old.byQuarter = old.byQuarter || {};
      old.byQuarter[q] = old.byQuarter[q] || {};
      switch (last.action) {
        case "goal":
          old.goals = Math.max(0, (old.goals || 0) - 1);
          old.byQuarter[q].goals = Math.max(0, (old.byQuarter[q].goals || 0) - 1);
          setHomeScore((n) => Math.max(0, n - 1));
          break;
        case "miss":
          old.misses = Math.max(0, (old.misses || 0) - 1);
          old.byQuarter[q].misses = Math.max(0, (old.byQuarter[q].misses || 0) - 1);
          break;
        case "oppositionGoal": setAwayScore((n) => Math.max(0, n - 1)); break;
        default:
          old[last.action] = Math.max(0, (old[last.action] || 0) - 1);
          old.byQuarter[q][last.action] = Math.max(0, (old.byQuarter[q][last.action] || 0) - 1);
          break;
      }
      if (!key) return prev;
      const nextStats = { ...prev, [key]: old };
      saveMatch({ stats: nextStats });
      return nextStats;
    });
  };

  const handleStart = () => setTimerRunning(true);
  const handlePause = () => setTimerRunning(false);
  const handleEndQuarter = () => {
    if (quarter >= 4) return;
    setTimerRunning(false);
    const now = Date.now();

    const updatedStats = { ...stats };
    const updatedOnCourt = onCourt.map((p) => {
      const played = Math.round((now - (p.onCourtSince || now)) / 60000) + (stats[p.playerId]?.minutes || 0);
      updatedStats[p.playerId] = { ...(updatedStats[p.playerId] || {}), minutes: played };
      return { ...p, totalMinutes: played, onCourtSince: now };
    });

    setStats(updatedStats);
    setOnCourt(updatedOnCourt);
    setQuarter((q) => q + 1);
    setTimer((match.quarterLength || 12) * 60);
    saveMatch({ stats: updatedStats, quarter: quarter + 1 });
  };

  // --- SHOOTER-FIRST SORT ---
  const sortOnCourt = (arr) => {
    const byPos = {};
    arr.forEach(p => byPos[p.position] = p);
    return positions.map(pos => byPos[pos]).filter(Boolean);
  };

  // --- Substitution Modal Logic ---
  const openSubModal = () => {
    setSubData({});
    setConfirmingSub(false);
    setShowSubModal(true);
  };

  const handleSubSelectOut = (player) => {
    setSubData({ playerOut: player });
  };

  const handleSubSelectIn = (player) => {
    setSubData((prev) => ({ ...prev, playerIn: player }));
    setConfirmingSub(true);
  };

  const handleCancelSub = () => {
    setShowSubModal(false);
    setSubData({});
    setConfirmingSub(false);
  };

  const handleConfirmSub = () => {
    const { playerOut, playerIn } = subData;
    if (!playerOut || !playerIn) return;

    const now = Date.now();

    // Record minutes for the player coming off
    const minutesPlayed = Math.round((now - (playerOut.onCourtSince || now)) / 60000) + (stats[playerOut.playerId]?.minutes || 0);
    const updatedStats = {
      ...stats,
      [playerOut.playerId]: { ...(stats[playerOut.playerId] || {}), minutes: minutesPlayed },
    };

    // Swap players between onCourt and bench
    const newOnCourt = onCourt.map((p) =>
      p.playerId === playerOut.playerId
        ? { ...playerIn, position: playerOut.position, onCourtSince: now, totalMinutes: stats[playerIn.playerId]?.minutes || 0 }
        : p
    );
    const newBench = bench
      .filter((p) => p.playerId !== playerIn.playerId)
      .concat({ ...playerOut, onCourtSince: null, totalMinutes: minutesPlayed });

    const subEvent = {
      action: "substitution",
      details: { playerOut: playerOut.playerId, playerIn: playerIn.playerId, position: playerOut.position },
      quarter,
      time: timer,
      ts: Date.now(),
    };
    const newEvents = [...events, subEvent];

    setStats(updatedStats);
    setOnCourt(newOnCourt);
    setBench(newBench);
    setEvents(newEvents);
    saveMatch({ stats: updatedStats, events: newEvents });

    handleCancelSub();
  };

  // --- Player Action Modal functions ---

  const handlePlayerAction = (action, player) => {
    recordEvent(action, player);
    setActivePlayerCard(null);
  };

  const formatTime = (sec) => `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
  const displayPlayers = sortOnCourt(onCourt);

  if (loading || !match || !team || !club) return <div className="loading">Loading match...</div>;

  return (
    <div className="match-screen">
      <div className="match-container">
        {/* Static Section - Header, Scoreboard, Controls */}
        <div className="match-static-section">
          {/* Header */}
          <div className="match-header">
            <h1 className="match-title">{club.clubName} — {team.teamName}</h1>
            <p className="match-opponent">vs. {match.opponent}</p>
          </div>

          {/* Combined Scoreboard with Quarter and Timer */}
          <div className="scoreboard-combined">
            <div className="match-info-compact">
              <span className="quarter-info">Q{quarter}</span>
              <span className="timer-info">{formatTime(timer)}</span>
            </div>
            <div className="score-display">
              <div className="team-score">
                <span className="team-name-compact">{team.teamName}</span>
                <span className="score-home">{homeScore}</span>
              </div>
              <span className="score-separator">—</span>
              <div className="team-score">
                <span className="score-away">{awayScore}</span>
                <span className="team-name-compact">Opposition</span>
              </div>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="controls">
            <button onClick={handleStart} className="btn btn-start">Start</button>
            <button onClick={handlePause} className="btn btn-pause">Pause</button>
            <button onClick={handleEndQuarter} className="btn btn-end-quarter">End Quarter</button>
            <button onClick={handleOppGoal} className="btn btn-opp-goal">Opp +1</button>
            <button onClick={handleUndo} className="btn btn-undo">Undo</button>
            <button onClick={openSubModal} className="btn btn-substitute">Substitute</button>
          </div>
        </div>

        {/* Scrollable Section - Players */}
        <div className="match-scrollable-section">
          <div className="players-section">
            <h3 className="players-title">Players (On Court)</h3>
            
            {/* Shooters Section */}
            <div className="shooters-section">
              <h4 className="section-title">Shooters</h4>
              <div className="shooters-grid">
                {displayPlayers.filter(p => shooterPositions.includes(p.position)).map((p) => {
                  const s = stats[p.playerId] || {};
                  const attempts = (s.goals || 0) + (s.misses || 0);
                  const success = attempts > 0 ? Math.round((s.goals || 0) * 100 / attempts) : 0;
                  const positives = positiveStats.reduce((sum, k) => sum + (s[k] || 0), 0);
                  const negatives = negativeStats.reduce((sum, k) => sum + (s[k] || 0), 0);

                  return (
                    <div key={p.playerId} className="player-card shooter">
                      {/* Player Header */}
                      <div className="player-header">
                        <span className="player-name">
                          {p.name} <span className="player-position">({p.position})</span>
                        </span>
                        <button
                          onClick={() => setExpandedStatsPlayer(p)}
                          className="stats-btn"
                        >
                          Stats
                        </button>
                      </div>
                      
                      <div className="player-minutes">Minutes played: {s.minutes || 0}</div>
                      
                      {/* Shooter stats quick view */}
                      <div className="shooter-stats">
                        Goals: {s.goals || 0} | Misses: {s.misses || 0} | Success %: {success}%
                      </div>
                      
                      {/* Totals quick view */}
                      <div className="player-totals">
                        <span>Positives: <span className="positive-total">{positives}</span></span>
                        <span>Negatives: <span className="negative-total">{negatives}</span></span>
                      </div>
                      
                      {/* Quick buttons for shooters */}
                      <div className="shooter-actions">
                        <button
                          onClick={() => recordEvent("goal", p)}
                          className="btn-goal"
                        >
                          Goal
                        </button>
                        <button
                          onClick={() => recordEvent("miss", p)}
                          className="btn-miss"
                        >
                          Miss
                        </button>
                      </div>
                      
                      {/* Actions button */}
                      <button
                        onClick={() => setActivePlayerCard(p)}
                        className="player-actions-btn shooter"
                      >
                        Actions
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Non-Shooters Section */}
            <div className="non-shooters-section">
              <h4 className="section-title">Court Players</h4>
              <div className="non-shooters-grid">
                {displayPlayers.filter(p => !shooterPositions.includes(p.position)).map((p) => {
                  const s = stats[p.playerId] || {};
                  const positives = positiveStats.reduce((sum, k) => sum + (s[k] || 0), 0);
                  const negatives = negativeStats.reduce((sum, k) => sum + (s[k] || 0), 0);

                  return (
                    <div key={p.playerId} className="player-card non-shooter">
                      {/* Player Header */}
                      <div className="player-header">
                        <span className="player-name">
                          {p.name} <span className="player-position">({p.position})</span>
                        </span>
                        <button
                          onClick={() => setExpandedStatsPlayer(p)}
                          className="stats-btn"
                        >
                          Stats
                        </button>
                      </div>
                      
                      <div className="player-minutes">Minutes: {s.minutes || 0}</div>
                      
                      {/* Totals quick view */}
                      <div className="player-totals">
                        <span>+{positives}</span>
                        <span>-{negatives}</span>
                      </div>
                      
                      {/* Actions button */}
                      <button
                        onClick={() => setActivePlayerCard(p)}
                        className="player-actions-btn regular"
                      >
                        Actions
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* --- EXPANDED STATS MODAL --- */}
        {expandedStatsPlayer && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h4 className="modal-title">
                  {expandedStatsPlayer.name} ({expandedStatsPlayer.position})
                </h4>
              </div>
              <div className="modal-content">
                {/* Shooter stats section */}
                {shooterPositions.includes(expandedStatsPlayer.position) && (() => {
                  const s = stats[expandedStatsPlayer.playerId] || {};
                  const attempts = (s.goals || 0) + (s.misses || 0);
                  const success = attempts > 0 ? Math.round((s.goals || 0) * 100 / attempts) : 0;
                  return (s.goals || s.misses) ? (
                    <div className="stats-section shooter">
                      <div className="stats-section-title">Shooter Stats</div>
                      {s.goals > 0 && <div className="stat-item">Goals: <b>{s.goals}</b></div>}
                      {s.misses > 0 && <div className="stat-item">Misses: <b>{s.misses}</b></div>}
                      {(s.goals || s.misses) > 0 && <div className="stat-item">Success %: <b>{success}%</b></div>}
                      {/* Per quarter for shooters */}
                      {Array.from({ length: 4 }).map((_, i) => {
                        const qGoals = (s.byQuarter && s.byQuarter[i + 1]?.goals) || 0;
                        const qMiss = (s.byQuarter && s.byQuarter[i + 1]?.misses) || 0;
                        if (qGoals || qMiss) {
                          return (
                            <div key={i} className="stat-item">
                              Q{i + 1}: {qGoals > 0 && <span className="quarter-chip positive">Goals: {qGoals}</span>}
                              {qMiss > 0 && <span className="quarter-chip negative">Miss: {qMiss}</span>}
                            </div>
                          );
                        } else return null;
                      })}
                    </div>
                  ) : null;
                })()}
                
                {/* Positive Actions */}
                {(() => {
                  const s = stats[expandedStatsPlayer.playerId] || {};
                  const positiveItems = positiveStats.filter(stat => (s[stat] || 0) > 0);
                  if (positiveItems.length === 0) return null;
                  return (
                    <div className="stats-section positive">
                      <div className="stats-section-title">Positive Actions</div>
                      {positiveItems.map(stat => (
                        <div key={stat} className="stat-item">
                          {statActions.find(a => a.key === stat)?.label || stat}: <b>{s[stat]}</b>
                          {Array.from({ length: 4 }).map((_, i) => {
                            const qVal = (s.byQuarter && s.byQuarter[i + 1]?.[stat]) || 0;
                            return qVal > 0 ? (
                              <span key={i} className="quarter-chip positive">
                                Q{i + 1}: {qVal}
                              </span>
                            ) : null;
                          })}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                
                {/* Negative Actions */}
                {(() => {
                  const s = stats[expandedStatsPlayer.playerId] || {};
                  const negativeItems = negativeStats.filter(stat => (s[stat] || 0) > 0);
                  if (negativeItems.length === 0) return null;
                  return (
                    <div className="stats-section negative">
                      <div className="stats-section-title">Negative Actions</div>
                      {negativeItems.map(stat => (
                        <div key={stat} className="stat-item">
                          {statActions.find(a => a.key === stat)?.label || stat}: <b>{s[stat]}</b>
                          {Array.from({ length: 4 }).map((_, i) => {
                            const qVal = (s.byQuarter && s.byQuarter[i + 1]?.[stat]) || 0;
                            return qVal > 0 ? (
                              <span key={i} className="quarter-chip negative">
                                Q{i + 1}: {qVal}
                              </span>
                            ) : null;
                          })}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                
                {/* Minutes played summary */}
                <div className="minutes-summary">
                  Minutes played: <b>{stats[expandedStatsPlayer.playerId]?.minutes || 0}</b>
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={() => setExpandedStatsPlayer(null)} className="btn-close">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* --- ACTION MODAL --- */}
        {activePlayerCard && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h4 className="modal-title">Log Stat for {activePlayerCard.name} ({activePlayerCard.position})</h4>
              </div>
              <div className="modal-content">
                {["Shooter", "Positive", "Negative"].map(section => (
                  <div key={section} className="action-section">
                    <div className="action-section-title">{section} Actions</div>
                    <div className="action-buttons">
                      {statActions.filter(a =>
                        a.section === section &&
                        (section !== "Shooter" || shooterPositions.includes(activePlayerCard.position))
                      ).map(action => (
                        <button
                          key={action.key}
                          onClick={() => handlePlayerAction(action.key, activePlayerCard)}
                          className={`action-btn ${section.toLowerCase()}`}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button onClick={() => setActivePlayerCard(null)} className="btn-close">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* --- SUBSTITUTION MODAL --- */}
        {showSubModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h4 className="modal-title">
                  {!subData.playerOut
                    ? "Select player to substitute OUT"
                    : confirmingSub
                    ? `Confirm: ${subData.playerIn?.name} replaces ${subData.playerOut?.name} (${subData.playerOut?.position})`
                    : "Select player to come ON"}
                </h4>
              </div>
              <div className="modal-content">
                {!subData.playerOut ? (
                  /* Step 1: pick who goes off */
                  <div className="action-buttons">
                    {sortOnCourt(onCourt).map((p) => (
                      <button
                        key={p.playerId}
                        onClick={() => handleSubSelectOut(p)}
                        className="action-btn negative"
                      >
                        {p.name} ({p.position})
                      </button>
                    ))}
                  </div>
                ) : !confirmingSub ? (
                  /* Step 2: pick who comes on */
                  <div className="action-buttons">
                    {bench.map((p) => (
                      <button
                        key={p.playerId}
                        onClick={() => handleSubSelectIn(p)}
                        className="action-btn positive"
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  /* Step 3: confirm */
                  <div className="action-buttons">
                    <button onClick={handleConfirmSub} className="action-btn positive">
                      Confirm Substitution
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button onClick={handleCancelSub} className="btn-close">Cancel</button>
              </div>
            </div>
          </div>
        )}

        <div className="back-section">
          <button onClick={() => navigate("/dashboard")} className="btn-back">Back to Dashboard</button>
        </div>
      </div>
    </div>
  );
}

export default MatchScreen;
